// WP-22 (D-F5 RESOLVED) — the ONE canonical Steier branding surface shared by
// the client-facing render paths (Worker email/PDF). LLD-01 §7.2 pins the
// organization.branding jsonb shape ("logo key/colours/pdf footer/email
// display"); O-15 (WP-17) is the tenant self-service writer. This module is the
// single source of truth for:
//   1. the Steier DEFAULT palette every tenant falls back to, and
//   2. resolveClientBranding() — merge a tenant's branding over the default for
//      a client-facing render.
//
// Tenancy: resolution is pure over the caller-supplied branding + the tenant's
// OWN organization.name. The display name for a client-facing email/PDF is the
// tenant's own trading identity — NEVER the literal "Steier" and NEVER another
// tenant's name (the Worker resolves ctx.org under the payload tenant's context;
// the leak suite proves A's render carries only A's identity).
//
// WP-UX-1: the default VALUES now come from the §10 token source
// (./design-tokens.ts) — identity navy, action-blue accent, white on-identity.
// Tenant colours are applied to the apps through the §10.1.1 colour guard
// (./colour-guard.ts); this module only resolves WHICH colours a tenant has.
import { COLOUR_ROLES } from "./design-tokens.js";
/**
 * The Steier platform default palette (D-F5 — one platform, Steier-branded).
 * These are the REAL defaults now (no longer "placeholder pending assets").
 * The field theme (apps/field/src/theme/theme.ts) and office-web
 * (apps/office-web/src/theme) IMPORT this constant via @steier/shared/contract
 * — no copies (P1-01). office-web's tokens.css carries the same values as
 * static CSS custom properties, drift-guarded by a test
 * (apps/office-web/test/theme-tokens.test.ts). Any tenant that sets no
 * branding renders with exactly these.
 */
export const STEIER_DEFAULT_BRANDING = {
    /** Steier slate navy — UX v1.6 §10.1 `colour.identity`. */
    primaryColor: COLOUR_ROLES.identity,
    /**
     * Steier action blue — §10.1 `colour.brand.accessible` (WP-UX-1, decision
     * row 67 iii: teal `#2E7D6B` is a tenant accent PRESET, no longer the default).
     */
    accentColor: COLOUR_ROLES["brand.accessible"],
    /** Contrast colour on the primary fill — §10.1 `colour.on.identity`. */
    onPrimary: COLOUR_ROLES["on.identity"],
    /** Wordmark shown when a tenant has no logo/display name of its own. */
    wordmark: "Steier",
};
// #rgb / #rrggbb — the only colour form persisted by O-15.
const HEX_COLOUR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
/**
 * The ONE hex-colour validator for branding values (P1-01: previously
 * re-implemented in the API, office-web and field). #rgb / #rrggbb only.
 */
export function isHexColour(value) {
    return typeof value === "string" && HEX_COLOUR.test(value);
}
function hex(value, fallback) {
    return isHexColour(value) ? value : fallback;
}
function nonEmpty(value) {
    return typeof value === "string" && value.trim().length > 0 ? value : null;
}
/**
 * Merge a tenant's branding over the Steier default for a client-facing render.
 * Invalid/missing values fall back key-by-key — a half-configured tenant never
 * yields a broken render.
 *
 * @param branding the tenant's organization.branding jsonb (any/unknown)
 * @param orgName  the tenant's own organization.name (the display fallback —
 *                 never another tenant's, never the platform name)
 */
export function resolveClientBranding(branding, orgName) {
    const b = (branding ?? {});
    return {
        primaryColor: hex(b.primaryColor, STEIER_DEFAULT_BRANDING.primaryColor),
        accentColor: hex(b.accentColor, STEIER_DEFAULT_BRANDING.accentColor),
        logoKey: nonEmpty(b.logoKey),
        displayName: nonEmpty(b.emailDisplay) ?? (orgName.trim().length > 0 ? orgName : STEIER_DEFAULT_BRANDING.wordmark),
        pdfFooter: nonEmpty(b.pdfFooter),
    };
}
//# sourceMappingURL=branding.js.map